/** The quote builder's toolbar and its two glyphs (moved out of `pages/quote-builder-v2.tsx` at step 8). */
import { cn } from '../lib/cn.js'
import { Button } from '../components/primitives.js'
import { IconChevronDown, IconPlus, IconSearch } from '../frame/icons.js'
import { ADD_CHOICES } from '../builder/copy.js'

// ---------------------------------------------------------------------------
// The toolbar
// ---------------------------------------------------------------------------

export function Toolbar({
  phone, canEdit, searchRef, packageRef, onSearch, onAddVisit, onAddPackage, onAddChoices,
  railOn, onRail, allCollapsed, onCollapseAll, sample, addVisitRef,
}: {
  /**
   * **+ Add choices** (step 21 item 4.5): an empty choice box, beside Add
   * visit and Add package, so the question can exist before any visit is in
   * it. Absent where a builder has no such box (the package builder's plans
   * are its own).
   */
  onAddChoices?: () => void
  /**
   * Given, **Add visit ▾** is the menu button of Board 10 (visits §5.1.1):
   * accent-soft, 38 high, a chevron, and the menu hangs off this element.
   */
  addVisitRef?: React.MutableRefObject<HTMLSpanElement | null>
  /** The package's **Try it with** — the sample every figure is worked out at (cb-05 ②). */
  sample?: React.ReactNode
  phone: boolean
  /** The totals rail is on screen. */
  railOn: boolean
  canEdit: boolean
  searchRef: React.MutableRefObject<HTMLButtonElement | null>
  packageRef: React.MutableRefObject<HTMLSpanElement | null>
  onSearch: () => void
  onAddVisit: () => void
  onAddPackage: () => void
  onRail?: () => void
  /** Every visit and service is collapsed — the pill reads on. */
  allCollapsed: boolean
  onCollapseAll: () => void
}) {
  return (
    <div className={cn('flex shrink-0 items-center gap-2.5 bg-bg px-4 pt-4 xl:px-6',
      phone && 'flex-wrap')}>
      {/*
        * The search is the primary way things get onto a quote, so it is first
        * and it is wide. ⌘/ rather than ⌘K because ⌘K is the app's own search
        * and a quote builder that stole it would break the one shortcut people
        * already know.
        */}
      {/* Absent, not disabled, on a quote that is not a draft (walkthrough findings §4). */}
      {canEdit && <button
        ref={searchRef}
        type="button"
        onClick={onSearch}
        className={cn(
          'flex items-center gap-2 border border-line bg-surface px-3',
          'text-[13px] text-text-faint hover:border-line-strong',
          /* Board 10: the search is 330 × 38 beside the Add visit menu. */
          addVisitRef && !phone ? 'h-[38px] rounded-[10px]' : 'h-9 rounded-field',
          phone ? 'min-w-0 flex-1' : addVisitRef ? 'w-[330px]' : 'w-[360px]',
        )}
      >
        <IconSearch size={15} />
        <span className="truncate">Add a service, item, labor…</span>
        <span className="ml-auto shrink-0 rounded-tag border border-line px-1.5 py-px text-[10.5px] font-semibold text-text-faint">
          ⌘/
        </span>
      </button>}

      {canEdit && addVisitRef && !phone && (
        <span ref={addVisitRef} className="shrink-0">
          <button
            type="button"
            aria-haspopup="menu"
            onClick={onAddVisit}
            className="flex h-[38px] items-center gap-[7px] rounded-[10px] border border-accent-line bg-accent-soft px-3
                       text-[13px] font-semibold text-accent-dark hover:bg-accent-soft/70"
          >
            <IconPlus size={15} />
            Add visit
            <IconChevronDown size={14} />
          </button>
        </span>
      )}
      {canEdit && (!addVisitRef || phone) && (
        <Button onClick={onAddVisit} className="h-9 shrink-0 gap-1.5 rounded-field">
          <IconPlus size={15} />
          Add visit
        </Button>
      )}
      {/*
        * Beside Add visit, because that is what it is: a package *is* visits,
        * often several, and adding one is closer to adding three trips than
        * to adding a line to a service.
        *
        * It used to live in the search with the services, which was the bug
        * behind Aaron's report — what the search offered under that name was
        * a *service* that happened to appear in some package's recipe, and
        * picking it put every line on one visit. A package now has its own
        * button and its own endpoint, and that endpoint has always made a
        * visit per visit.
        */}
      {canEdit && (
        /*
         * A `span` around it rather than a ref on it: `Button` does not
         * forward one, and the popover only needs a rectangle to hang off.
         */
        <span ref={packageRef} className="shrink-0">
          <Button onClick={onAddPackage}
            className={addVisitRef && !phone ? 'h-[38px] gap-1.5 rounded-[10px]' : 'h-9 gap-1.5 rounded-field'}>
            <IconPlus size={15} />
            Add package
          </Button>
        </span>
      )}

      {canEdit && onAddChoices && (
        <Button onClick={onAddChoices}
          className={addVisitRef && !phone ? 'h-[38px] shrink-0 gap-1.5 rounded-[10px]' : 'h-9 shrink-0 gap-1.5 rounded-field'}>
          <IconPlus size={15} />
          {ADD_CHOICES}
        </Button>
      )}

      {sample}

      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        {/*
          * Two switches, each on or off (Aaron): Prices & costs is the
          * rail, and Collapse all is every visit and service folded — on
          * folds them, off opens them all again. Before, Collapse all was
          * never drawn on, so it could not say which state it was in.
          */}
        {!phone && onRail && <Pill on={railOn} onClick={onRail}>Prices &amp; costs</Pill>}
        {!phone && <Pill on={allCollapsed} onClick={onCollapseAll}>Collapse all</Pill>}
      </div>
    </div>
  )
}

/** The toolbar's on/off pill — exported so the Billing tab's toolbar is the same control. */
export function Pill({
  on, onClick, children,
}: {
  on: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-chip px-2.5',
        'text-[11.5px] font-semibold',
        on ? 'bg-text text-surface' : 'border border-line bg-surface text-text-body',
      )}
    >
      {children}
    </button>
  )
}


/**
 * The tag a package's visit wears, beside the auto-named one.
 *
 * Inside the group's tint it is nearly redundant — and then somebody collapses
 * the rail, or reads the quote on a phone where the group is a heading rather
 * than a border, and the card has to say for itself where it came from.
 */
export function PackageChip() {
  return (
    <span className={cn('shrink-0 rounded-tag bg-accent-soft px-1.5 py-px',
      'text-[10.5px] font-semibold text-accent-dark')}>
      from package
    </span>
  )
}
